import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useSettings } from "../context/ThemeContext";
import { useLang } from "../context/LanguageContext";
import BenovaLogo from "./BenovaLogo";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();
  const { lang, toggle, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const onHome = location.pathname === "/";
  const useLight = onHome && !scrolled;
  const navColor = useLight ? "#F5F3EC" : "var(--bn-text)";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled || !onHome ? "bn-glass" : ""}`}
      style={{ borderBottom: scrolled || !onHome ? "1px solid var(--bn-border)" : "1px solid transparent" }}
    >
      <div className="bn-container flex items-center justify-between px-5 md:px-8 py-4 md:py-5">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          <BenovaLogo color={navColor} size="md" accent={useLight ? "#C9B98A" : "var(--bn-accent)"} />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" className="bn-eyebrow hover:opacity-70" style={{ color: navColor }} data-testid="nav-home">{t("nav.home")}</Link>
          <Link to="/experiencias" className="bn-eyebrow hover:opacity-70" style={{ color: navColor }} data-testid="nav-experiences">{t("nav.experiences")}</Link>
          <a href="#contacto" className="bn-eyebrow hover:opacity-70" style={{ color: navColor }} data-testid="nav-contact">{t("nav.contact")}</a>

          <button
            onClick={toggle}
            className="bn-eyebrow inline-flex items-center gap-1.5 px-3 py-1.5 transition"
            style={{ color: navColor, border: `1px solid ${useLight ? "rgba(245,243,236,0.35)" : "var(--bn-border)"}`, borderRadius: "var(--bn-button-radius)" }}
            data-testid="lang-toggle"
            aria-label="language"
          >
            <Globe size={12} /> {lang.toUpperCase()} / {lang === "es" ? "EN" : "ES"}
          </button>

          {settings.contact_whatsapp && (
            <a href={`https://wa.me/${settings.contact_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="bn-btn-primary" data-testid="nav-book-btn">
              {t("nav.book")}
            </a>
          )}
        </nav>

        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggle}
            className="bn-eyebrow inline-flex items-center gap-1 px-2.5 py-1.5"
            style={{ color: navColor, border: `1px solid ${useLight ? "rgba(245,243,236,0.35)" : "var(--bn-border)"}`, borderRadius: "var(--bn-button-radius)" }}
            data-testid="lang-toggle-mobile"
          >
            <Globe size={12} /> {lang.toUpperCase()}
          </button>
          <button className="p-2" onClick={() => setOpen(!open)} style={{ color: navColor }} data-testid="navbar-menu-toggle" aria-label="menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bn-glass border-t" style={{ borderColor: "var(--bn-border)" }}>
          <div className="flex flex-col px-6 py-6 gap-5">
            <Link to="/" className="bn-eyebrow" style={{ color: "var(--bn-text)" }} data-testid="nav-mobile-home">{t("nav.home")}</Link>
            <Link to="/experiencias" className="bn-eyebrow" style={{ color: "var(--bn-text)" }} data-testid="nav-mobile-experiences">{t("nav.experiences")}</Link>
            <a href="#contacto" className="bn-eyebrow" style={{ color: "var(--bn-text)" }} data-testid="nav-mobile-contact">{t("nav.contact")}</a>
            {settings.contact_whatsapp && (
              <a href={`https://wa.me/${settings.contact_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="bn-btn-primary text-center" data-testid="nav-mobile-book-btn">
                {t("nav.book")}
              </a>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
